import {
  NOTIFICATION_ADD,
  NOTIFICATION_REMOVE,
  NOTIFICATION_MARK_READ,
  NOTIFICATION_MARK_ALL_READ,
  NOTIFICATION_CLEAR_ALL,
  NOTIFICATION_SET_MUTE,
  NOTIFICATION_SET,
  NOTIFICATION_RESET,
} from "../constants/action-types";
import { Notification } from "../types/notification.type";

export function addNotification(notification: Partial<Notification>) {
  return {
    type: NOTIFICATION_ADD,
    notification,
  };
}

export function removeNotification(id: string) {
  return {
    type: NOTIFICATION_REMOVE,
    id,
  };
}

export function markNotificationAsRead(id: string) {
  return {
    type: NOTIFICATION_MARK_READ,
    id,
  };
}

export function markAllNotificationsAsRead() {
  return {
    type: NOTIFICATION_MARK_ALL_READ,
  };
}

export function clearAllNotifications() {
  return {
    type: NOTIFICATION_CLEAR_ALL,
  };
}

export function setNotificationMute(muteNotifications: boolean) {
  return {
    type: NOTIFICATION_SET_MUTE,
    muteNotifications,
  };
}

export function setNotifications(notifications: Notification[]) {
  return {
    type: NOTIFICATION_SET,
    notifications,
  };
}

export function resetNotifications() {
  return {
    type: NOTIFICATION_RESET,
  };
}

// Helper functions to create missed contact notifications
export function createMissedCallNotification(contactId: string, customerName: string, phoneNumber: string, queueName?: string) {
  return {
    type: 'call' as const,
    title: `Missed Call from ${customerName || phoneNumber}`,
    description: `You missed an incoming call from ${customerName || phoneNumber}${queueName ? ` in queue ${queueName}` : ''}.`,
    priority: 'high' as const,
    category: 'missed_call',
    timestamp: new Date().toISOString(),
    actionPayload: { 
      contactId, 
      customerName, 
      phoneNumber, 
      queueName,
      contactType: 'call',
      missedAt: new Date().toISOString()
    },
  };
}

// Helper functions to create notification for changin agent state to Not Ready due to missed contact
export function createMissedAgentStateAvailableNotification() {
  return {
    type: 'agent_state_available' as const,
    title: `Agent State Changed to Not Ready`,
    description: `Agent state changed to Not Ready due to missed contact.`,
    priority: 'high' as const,
    category: 'agent_state_available',
    timestamp: new Date().toISOString(),
    actionPayload: {
      missedAt: new Date().toISOString()
    },
  };
}

export function createMissedChatNotification(contactId: string, customerName: string, queueName?: string) {
  return {
    type: 'chat' as const,
    title: `Missed Chat from ${customerName}`,
    description: `You missed an incoming chat from ${customerName}${queueName ? ` in queue ${queueName}` : ''}.`,
    priority: 'high' as const,
    category: 'missed_chat',
    timestamp: new Date().toISOString(),
    actionPayload: { 
      contactId, 
      customerName, 
      queueName,
      contactType: 'chat',
      missedAt: new Date().toISOString()
    },
  };
}

export function createMissedCallbackNotification(contactId: string, customerName: string, phoneNumber: string, timestamp: string) {
  return {
    type: 'callback' as const,
    title: `Missed Callback for ${customerName}`,
    description: `You missed a callback for ${customerName} (${phoneNumber}).`,
    priority: 'high' as const,
    category: 'missed_callback',
    timestamp: new Date().toISOString(),
    actionPayload: { 
      contactId, 
      customerName, 
      phoneNumber, 
      timestamp,
      contactType: 'callback',
      missedAt: new Date().toISOString()
    },
  };
}

export function createMissedVoicemailNotification(contactId: string, customerName: string, phoneNumber: string, duration: number) {
  return {
    type: 'voicemail' as const,
    title: `New Voicemail from ${customerName || phoneNumber}`,
    description: `You have missed a voicemail from (${phoneNumber}).`,
    priority: 'medium' as const,
    category: 'voicemail',
    timestamp: new Date().toISOString(),
    actionPayload: { 
      contactId, 
      customerName: customerName || phoneNumber, 
      phoneNumber, 
      duration,
      contactType: 'voicemail',
      receivedAt: new Date().toISOString()
    },
  };
}

export function createMissedCaseNotification(contactId: string, customerName: string, queueName?: string) {
  return {
    type: 'case' as const,
    title: `Missed Case from ${customerName}`,
    description: `You missed a case from ${customerName}${queueName ? ` in queue ${queueName}` : ''}.`,
    priority: 'medium' as const,
    category: 'missed_case',
    timestamp: new Date().toISOString(),
    actionPayload: { 
      contactId, 
      customerName,
      queueName,
      contactType: 'case',
      missedAt: new Date().toISOString()
    },
  };
}

// Generic function to create missed contact notification
export function createMissedContactNotification(
  contactId: string, 
  customerName: string, 
  contactType: 'call' | 'chat' | 'callback' | 'voicemail' | 'case',
  additionalDetails: {
    phoneNumber?: string;
    queueName?: string;
    duration?: number;
  } = {}
) {
  const baseNotification = {
    title: `Missed ${contactType.charAt(0).toUpperCase() + contactType.slice(1)} from ${customerName}`,
    priority: 'high' as const,
    timestamp: new Date().toISOString(),
    actionPayload: { 
      contactId, 
      customerName, 
      contactType,
      missedAt: new Date().toISOString(),
      ...additionalDetails
    },
  };

  switch (contactType) {
    case 'call':
      return {
        ...baseNotification,
        type: 'call' as const,
        description: `You missed an incoming call from ${customerName}${additionalDetails.phoneNumber ? ` (${additionalDetails.phoneNumber})` : ''}${additionalDetails.queueName ? ` in queue ${additionalDetails.queueName}` : ''}.`,
        category: 'missed_call',
      };
    case 'chat':
      return {
        ...baseNotification,
        type: 'chat' as const,
        description: `You missed an incoming chat from ${customerName}${additionalDetails.queueName ? ` in queue ${additionalDetails.queueName}` : ''}.`,
        category: 'missed_chat',
      };
    case 'callback':
      return {
        ...baseNotification,
        type: 'callback' as const,
        description: `You missed a callback for ${customerName}${additionalDetails.phoneNumber ? ` (${additionalDetails.phoneNumber})` : ''}.`,
        category: 'missed_callback',
      };
    case 'voicemail':
      return {
        ...baseNotification,
        type: 'voicemail' as const,
        description: `You missed a voicemail from ${additionalDetails.phoneNumber ? ` (${additionalDetails.phoneNumber})` : ''}.`,
        category: 'voicemail',
        priority: 'medium' as const,
      };
    case 'case':
      return {
        ...baseNotification,
        type: 'case' as const,
        description: `You missed a case from ${customerName}${additionalDetails.queueName ? ` in queue ${additionalDetails.queueName}` : ''}.`,
        category: 'missed_case',
        priority: 'medium' as const,
      };
    default:
      return baseNotification;
  }
}
